<template>
  <div class="sso-manage">
    <div class="content-box">
      <div class="doc-container">
        <!-- 左侧目录导航 -->
        <div class="doc-nav">
          <div class="nav-title">目录</div>
          <ul class="nav-list">
            <li 
              v-for="(heading, index) in headings" 
              :key="index"
              @click="scrollToHeading(heading && heading.id)"
              :class="{ 
                active: activeHeading === (heading && heading.id),
                'level-1': heading && heading.level === 1,
                'level-2': heading && heading.level === 2,
                'level-3': heading && heading.level === 3
              }"
            >
              <i 
                :class="{
                  'el-icon-document': heading && heading.level === 1,
                  'el-icon-files': heading && heading.level === 2,
                  'el-icon-document-copy': heading && heading.level === 3
                }"
                class="heading-icon"
              ></i>
              {{ heading && heading.text }}
            </li>
          </ul>
        </div>

        <!-- 右侧Markdown内容 -->
        <div class="doc-content" ref="docContent">
          <div v-if="loading" class="loading-container">
            <i class="el-icon-loading"></i>
            <p>{{$t('openPlatform.loadingDocument')}}</p>
          </div>
          <div v-else class="markdown-body" v-html="renderedContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import anchor from 'markdown-it-anchor';
import 'github-markdown-css';

export default {
  name: 'SSOManage',
  data() {
    return {
      headings: [],
      activeHeading: '',
      md: null,
      markdownContent: '',
      loading: true
    }
  },
  computed: {
    renderedContent() {
      if (!this.md || !this.markdownContent || typeof this.markdownContent !== 'string') return '';
      try {
        return this.md.render(this.markdownContent);
      } catch (error) {
        console.error('渲染Markdown内容失败:', error);
        return '';
      }
    }
  },
  watch: {
    renderedContent: {
      handler(newVal, oldVal) {
        // 当内容渲染完成后，重新提取标题
        if (newVal && newVal !== oldVal) {
          this.$nextTick(() => {
            this.extractHeadings();
          });
        }
      },
      immediate: false
    }
  },
  mounted() {
    this.initMarkdownIt();
    this.loadMarkdownFile();
    
    // 调试：检查样式是否正确加载
    console.log('GitHub Markdown CSS 已导入');
  },
  methods: {
    // 初始化markdown-it
    initMarkdownIt() {
      try {
        this.md = new MarkdownIt({
          html: true,
          linkify: true,
          typographer: true,
          breaks: true
        });
        
        // 添加锚点插件
        if (anchor && typeof anchor === 'function') {
          this.md.use(anchor, {
            permalink: true,
            permalinkBefore: true,
            permalinkSymbol: '§',
            level: [1, 2, 3],
            slugify: (str) => {
              if (!str || typeof str !== 'string') return '';
              return str
                .toLowerCase()
                .replace(/[^a-z0-9\u4e00-\u9fa5]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
            }
          });
        }
      } catch (error) {
        console.error('初始化markdown-it失败:', error);
        // 降级到简单的HTML渲染
        this.md = {
          render: (content) => {
            if (!content || typeof content !== 'string') return '';
            return content
              .replace(/\n/g, '<br>')
              .replace(/^# (.*$)/gim, '<h1>$1</h1>')
              .replace(/^## (.*$)/gim, '<h2>$1</h2>')
              .replace(/^### (.*$)/gim, '<h3>$1</h3>');
          }
        };
      }
    },

    // 加载Markdown文件
    async loadMarkdownFile() {
      try {
        this.loading = true;
        const response = await fetch('/md/sso.md');
        if (response && response.ok) {
          const text = await response.text();
          if (text && typeof text === 'string') {
            this.markdownContent = text;
          } else {
            throw new Error('返回的内容格式不正确');
          }
        } else {
          throw new Error(`HTTP error! status: ${response ? response.status : 'unknown'}`);
        }
      } catch (error) {
        console.error('加载Markdown文件失败:', error);
        // 降级到默认内容
        this.markdownContent = `# 单点登录接口文档

## 概述

本文档描述了单点登录（SSO）系统的API接口，包括用户认证、会话管理、权限验证等功能。

## 加载失败

由于无法加载本地Markdown文件，请检查文件路径或联系管理员。

错误信息：${error.message || '未知错误'}`;
      } finally {
        this.loading = false;
        // 内容加载完成后，设置滚动监听器
        this.$nextTick(() => {
          this.setupScrollListener();
        });
      }
    },

    // 提取标题
    extractHeadings() {
      // 等待DOM更新后提取标题
      this.$nextTick(() => {
        if (this.$refs.docContent) {
          const headingElements = this.$refs.docContent.querySelectorAll('h1, h2, h3');
          this.headings = [];
          
          headingElements.forEach((element, index) => {
            if (element && element.textContent) {
              const text = element.textContent;
              const id = element.id || this.generateId(text);
              const level = parseInt(element.tagName.charAt(1)); // 获取标题级别 (1, 2, 3)
              
              this.headings.push({ text, id, level });
            }
          });
        }
      });
    },

    // 生成ID
    generateId(text) {
      if (!text || typeof text !== 'string') return '';
      return text
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fa5]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    },

    // 滚动到指定标题
    scrollToHeading(id) {
      if (!id) return;
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        this.activeHeading = id;
      }
    },

    // 设置滚动监听
    setupScrollListener() {
      const content = this.$refs.docContent;
      if (content && !this.loading) {
        content.addEventListener('scroll', this.handleScroll);
      }
    },

    // 处理滚动
    handleScroll() {
      const content = this.$refs.docContent;
      const headings = this.headings;
      
      if (!content || !headings || headings.length === 0) return;
      
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        const element = document.getElementById(heading.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            this.activeHeading = heading.id;
            break;
          }
        }
      }
    }
  },
  beforeDestroy() {
    const content = this.$refs.docContent;
    if (content && this.handleScroll) {
      content.removeEventListener('scroll', this.handleScroll);
    }
  }
}
</script>

<style scoped lang="less">
.sso-manage {
  height: 100%;
  padding: 0;
  
  .content-box {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  
  .page-header {
    margin-bottom: 24px;
    padding: 0 20px;
    
    h2 {
      font-size: 20px;
      color: #303133;
      margin: 0 0 8px 0;
    }
    
    p {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }
  
  .doc-container {
    display: flex;
    height: calc(100vh - 400px);
    flex: 1;
  }

  .doc-nav {
    width: 250px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #ebeef5;
    padding: 20px;
    margin-right: 20px;
    height: fit-content;
    position: sticky;
    height: calc(100vh - 150px);
    overflow-y: auto;
    box-shadow: 0px 0px 18px 0px rgba(29, 55, 129, 0.07);

    .nav-title {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #ebeef5;
    }

    .nav-list {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        padding: 8px 12px;
        margin-bottom: 4px;
        border-radius: 4px;
        cursor: pointer;
        color: #606266;
        font-size: 14px;
        transition: all 0.3s;
        position: relative;

        /* 一级标题样式 */
        &.level-1 {
          font-weight: 600;
          font-size: 15px;
          color: #303133;
          padding-left: 12px;
          border-left: 3px solid transparent;
          
          .heading-icon {
            margin-right: 8px;
            color: #366aff;
            font-size: 16px;
          }
          
          &:hover {
            background-color: #f0f2f5;
            color: #366aff;
            border-left-color: #366aff;
          }
          
          &.active {
            background-color: #366aff;
            color: #fff;
            border-left-color: #fff;
            
            .heading-icon {
              color: #fff;
            }
          }
        }

        /* 二级标题样式 */
        &.level-2 {
          font-weight: 500;
          font-size: 14px;
          color: #606266;
          padding-left: 24px;
          border-left: 2px solid transparent;
          
          .heading-icon {
            margin-right: 8px;
            color: #67c23a;
            font-size: 14px;
          }
          
          &:hover {
            background-color: #f5f7fa;
            color: #366aff;
            border-left-color: #366aff;
          }
          
          &.active {
            background-color: #366aff;
            color: #fff;
            border-left-color: #fff;
            
            .heading-icon {
              color: #fff;
            }
          }
        }

        /* 三级标题样式 */
        &.level-3 {
          font-weight: 400;
          font-size: 13px;
          color: #909399;
          padding-left: 36px;
          border-left: 1px solid transparent;
          
          .heading-icon {
            margin-right: 8px;
            color: #e6a23c;
            font-size: 12px;
          }
          
          &:hover {
            background-color: #fafafa;
            color: #366aff;
            border-left-color: #366aff;
          }
          
          &.active {
            background-color: #366aff;
            color: #fff;
            border-left-color: #fff;
            
            .heading-icon {
              color: #fff;
            }
          }
        }

        /* 通用悬停效果 */
        &:hover {
          background-color: #f5f7fa;
          color: #366aff;
        }

        /* 通用激活状态 */
        &.active {
          background-color: #366aff;
          color: #fff;
        }
      }
    }
  }

  .doc-content {
    flex: 1;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #ebeef5;
    padding: 30px;
    overflow-y: auto;
    height: calc(100vh - 150px);
    box-shadow: 0px 0px 18px 0px rgba(29, 55, 129, 0.07);

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: #909399;
      
      i {
        font-size: 32px;
        margin-bottom: 16px;
        animation: rotate 1s linear infinite;
      }
      
      p {
        font-size: 14px;
        margin: 0;
      }
    }

    /* 使用 GitHub Markdown 样式 */
    .markdown-body {
      /* 确保 GitHub 样式生效 */
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
      font-size: 16px;
      line-height: 1.5;
      word-wrap: break-word;
      
      /* 标题样式 */
      h1, h2, h3 {
        border-bottom: 1px solid #eaecef;
        padding-bottom: 0.3em;
        margin-top: 24px;
        margin-bottom: 16px;
        font-weight: 600;
        line-height: 1.25;
      }
      
      h1 {
        font-size: 2em;
        border-bottom: 2px solid #eaecef;
      }
      
      h2 {
        font-size: 1.5em;
      }
      
      h3 {
        font-size: 1.25em;
      }
      
      /* 段落样式 */
      p {
        margin-top: 0;
        margin-bottom: 16px;
      }
      
      /* 表格样式优化 */
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 16px 0;
        display: block;
        overflow-x: auto;
        
        th, td {
          border: 1px solid #dfe2e5;
          padding: 6px 13px;
        }
        
        th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
        
        tr:nth-child(2n) {
          background-color: #f6f8fa;
        }
      }
      
      /* 代码块样式优化 */
      pre {
        background-color: #f6f8fa;
        border: 1px solid #e1e4e8;
        border-radius: 6px;
        padding: 16px;
        overflow: auto;
        font-size: 85%;
        line-height: 1.45;
        
        code {
          background: transparent;
          padding: 0;
          border: 0;
          font-size: 100%;
        }
      }
      
      /* 行内代码样式 */
      code {
        background-color: rgba(27, 31, 35, 0.05);
        border-radius: 3px;
        font-size: 85%;
        margin: 0;
        padding: 0.2em 0.4em;
        font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
      }
      
      /* 引用块样式 */
      blockquote {
        border-left: 4px solid #dfe2e5;
        color: #6a737d;
        margin: 0 0 16px 0;
        padding: 0 1em;
      }
      
      /* 链接样式 */
      a {
        color: #0366d6;
        text-decoration: none;
        
        &:hover {
          text-decoration: underline;
        }
      }
      
      /* 列表样式 */
      ul, ol {
        padding-left: 2em;
        margin-top: 0;
        margin-bottom: 16px;
      }
      
      /* 水平线样式 */
      hr {
        height: 0.25em;
        padding: 0;
        margin: 24px 0;
        background-color: #e1e4e8;
        border: 0;
      }
      
      /* 强调样式 */
      strong {
        font-weight: 600;
      }
      
      em {
        font-style: italic;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 